import { useEffect, useState } from 'react';
import { api } from '@/client/lib/api';

// How many finds are public (GET /api/finds/summary), fetched ONCE per visit
// and shared by every consumer (Header, MobileNavigation, Footer, the /shop
// hub): a module-level promise + its resolved value, so a remount paints the
// answer at once instead of flashing the entry in and out.
let pending: Promise<number> | null = null;
let resolved: number | null = null;

function loadFindsLive(): Promise<number> {
  pending ??= api
    .findsSummary()
    .then((s) => (typeof s?.live === 'number' && Number.isFinite(s.live) ? s.live : 0))
    // A failed read hides the entry (never an error in the menu).
    .catch(() => 0)
    .then((n) => (resolved = n));
  return pending;
}

/**
 * The live finds count, or null while unknown (not answered yet). Callers show
 * the «Hallazgos» entry only when it is > 0: hidden while loading, on error
 * and with no finds (see `showsFinds`).
 */
export function useFindsLive(): number | null {
  const [live, setLive] = useState<number | null>(resolved);
  useEffect(() => {
    if (resolved !== null) {
      setLive(resolved);
      return;
    }
    let alive = true;
    void loadFindsLive().then((n) => {
      if (alive) setLive(n);
    });
    return () => {
      alive = false;
    };
  }, []);
  return live;
}

/** Whether the conditional «Hallazgos» entry is shown (> 0 finds; unknown = hidden). */
export function showsFinds(live: number | null): boolean {
  return live !== null && live > 0;
}

/** Tests only: forget the cached answer so the next render fetches again. */
export function resetFindsLiveCache(): void {
  pending = null;
  resolved = null;
}
